import React, { useEffect, useState } from 'react';
import { Box, Button, Spinner, Text, CheckBox } from 'grommet';
import DrawingTools from './DrawingTools';
import { MAP_MODES } from './modes/modes';
import { useTranslation } from 'react-i18next';
import {
  addFieldAction,
  focusField,
  setDrawnPaddocksForField,
  setUploadingDrawnField,
  startSplittingField,
  updateField,
} from '../../../../redux/actions/map-actions/field/field-action';
import { useDispatch } from 'react-redux';
import { restartDraw, setMode, toggleTraining } from '../../../../redux/actions/map-actions/map-action';
import { useAppSelector } from '../../../../redux';
import { StyledTooltip } from '../../../../components/common/StyledTooltip';

const buttonStyle: React.CSSProperties = {
  paddingBottom: '0.5rem',
  paddingTop: '0.5rem',
  paddingRight: '0',
  paddingLeft: '0',
  width: '5rem',
  fontSize: '14px',
  border: 'none',
  borderRadius: '0px',
  color: '#191408',
};

const DrawingPanel = ({
  allowBoundaryEdit,
  setShowUploadModal,
  setShowConfirmationModal,
}: {
  allowBoundaryEdit: boolean;
  setShowUploadModal: (show: boolean) => void;
  setShowConfirmationModal: (show: boolean) => void;
}) => {
  const { t } = useTranslation();
  const dispatch = useDispatch();

  const wizardStep = 'FIELD'; // TODO fixing this as FIELDS because grazing is disabled
  const fields = useAppSelector((state) => state.map.fields);
  const isTraining = useAppSelector((state) => state.map.isDrawingTraining);
  const finishedDrawingFields = useAppSelector((state) => state.map.finishedDrawingFields);
  const finishedDrawingPaddocks = useAppSelector((state) => state.map.finishedDrawingPaddocks);
  const uploadingDrawnField = useAppSelector((state) => state.map.uploadingDrawnField);

  const [isEditingField, setEditingField] = useState<boolean>(false);

  useEffect(() => {
    setEditingField(!!fields.find((field) => field.properties?.['editing']));
  }, [fields]);

  function getButtonLabel() {
    if (uploadingDrawnField) {
      return (
        <Box align={'center'}>
          <Spinner color={wizardStep === 'FIELD' ? '#282828' : '#FFF'} size={'xsmall'} />
        </Box>
      );
    }
    if (wizardStep === 'FIELD') {
      return isEditingField ? t('fieldFlow.drawingPanel.update') : t('fieldFlow.drawingPanel.add');
    }
    return t('fieldFlow.drawingPanel.done');
  }

  return (
    <Box direction={'row'} background={'#fff'} pad={'0.6rem 1rem'} responsive={true} style={{ borderRadius: '5px', pointerEvents: 'auto' }}>
      <Box>
        <Box direction={'row'} justify={'between'}>
          <Text weight='bold' size='14px' alignSelf='start' color='#282828'>
            {t('fieldFlow.drawingPanel.title')}
          </Text>
          <Box direction={'row'} gap={'small'}>
            <CheckBox
              toggle={true}
              checked={isTraining}
              onChange={() => {
                dispatch(toggleTraining(!isTraining));
              }}
            />
            <Text weight='bold' size='14px' alignSelf='start' color='#282828'>
              Draw training region
            </Text>
          </Box>
        </Box>
        <StyledTooltip dropOptions={'top'} label={t('fieldFlow.drawingPanel.disabled')} showToolTip={!allowBoundaryEdit}>
          <fieldset
            style={{
              display: 'flex',
              flexDirection: 'row',
              margin: '0.5rem 0 0 0',
              border: 'none',
              padding: '0',
              opacity: allowBoundaryEdit ? '1' : '0.7',
            }}
            disabled={!allowBoundaryEdit}>
            <Box alignSelf='start' style={{ flexDirection: 'row', justifyContent: 'space-between' }}>
              <DrawingTools setShowUploadModal={setShowUploadModal} setShowConfirmationModal={setShowConfirmationModal} />
            </Box>
            <Box alignSelf='end' direction='row' gap='small'>
              <Button
                data-cy={'add-field-button'}
                label={getButtonLabel()}
                fill={true}
                disabled={wizardStep === 'FIELD' ? !finishedDrawingFields : !finishedDrawingPaddocks}
                style={{
                  ...buttonStyle,
                  background: wizardStep === 'FIELD' ? '#FFE137' : '#1B7C3D',
                  color: wizardStep === 'FIELD' ? '#282828' : '#FFF',
                }}
                onClick={() => {
                  // dispatch(setUploadingDrawnField(true));
                  if (wizardStep === 'FIELD') {
                    if (isEditingField) {
                      dispatch(updateField());
                    } else {
                      dispatch(addFieldAction());
                    }
                  } else {
                    dispatch(setMode(MAP_MODES.STATIC));
                    dispatch(restartDraw());
                    const field = fields.find((field) => field.properties && field.properties['editing']);
                    if (!field) {
                      dispatch(setUploadingDrawnField(false));
                      return;
                    }
                    dispatch(startSplittingField(''));
                    dispatch(setDrawnPaddocksForField(field));
                    dispatch(focusField(field.id));
                  }
                }}
              />
            </Box>
          </fieldset>
        </StyledTooltip>
      </Box>
      {wizardStep !== 'FIELD' && (
        <Box pad={'0 1rem'}>
          <ul>
            <li>
              <Text weight='bold' size='14px' alignSelf='start' color='#282828'>
                {t('fieldFlow.drawingPanel.instructions.drawBoundary')}
              </Text>
            </li>
            <li>
              <Text weight='bold' size='14px' alignSelf='start' color='#282828'>
                {t('fieldFlow.drawingPanel.deletePoint')}
              </Text>
            </li>
            <li>
              <Text weight='bold' size='14px' alignSelf='start' color='#282828'>
                {t('fieldFlow.drawingPanel.instructions.endLine')}
              </Text>
            </li>
          </ul>
        </Box>
      )}
    </Box>
  );
};

export default DrawingPanel;
